/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from '@teko/auth-react';
import { CalculatedPrice, SelectionPromotion } from '@teko/cart/v2';
import { logger } from '@teko/log';
import { useServices, useTenantConfig, useToast } from 'contexts';
import { t } from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

export interface AddToCartPayload {
  sku: string;
  sellerSku?: string;
  quantity?: number;
  isPrimary?: boolean;
}

export interface ProductPrice {
  discountPercent?: string;
  finalPrice?: number;
  displayRowTotal: string;
  strikeThroughDisplayRowTotal: string;
  rowTotal?: number;
  supplierRetailPrice?: number;
}

export const useCalculatePrice = (
  sku: string,
  defaultValue?: CalculatedPrice | null,
  productAvailable?: boolean,
  selectPromotionId?: string,
  isReturnVariants?: boolean,
) => {
  const { cartServiceV2 } = useServices();
  const { isMobile } = useTenantConfig();
  const toast = useToast(isMobile);

  const [payloadAddToCart, setPayloadAddToCart] = useState<AddToCartPayload>({
    sku,
    quantity: 1,
    isPrimary: undefined,
  });

  const [calculatePrice, setCalculatePrice] = useState<
    CalculatedPrice | null | undefined
  >(defaultValue);
  const [isLoading, setLoading] = useState(false);

  const { quantity } = payloadAddToCart;
  // hotfix for missing token when get price and promotion
  const { user } = useAuth();

  const { data: dataCalculatePrice, isValidating } = useSWR(
    productAvailable
      ? [
          `cart_service/get_price`,
          sku,
          quantity,
          selectPromotionId,
          user?.id,
          isReturnVariants,
        ]
      : null,
    async (_, sku, quantity: number) => {
      return cartServiceV2.getPriceAndPromotion(sku, {
        quantity,
        ...(!!selectPromotionId && !Number.isNaN(Number(selectPromotionId))
          ? { selectedPromotionId: Number(selectPromotionId) }
          : {}),
        isReturnVariants,
      });
    },
    {
      revalidateOnFocus: false,
      fallback: { ...defaultValue },
    },
  );

  const selectedPromotion = useMemo(() => {
    try {
      const promotions = calculatePrice?.promotions
        .map((p) => p.promotions)
        .reduce((a, b) => [...a, ...b], []);

      return promotions?.find((p) => p.isSelectable && p.isSelected);
    } catch {
      return null;
    }
  }, [JSON.stringify(calculatePrice)]);

  useEffect(() => {
    if (!productAvailable) {
      setCalculatePrice(defaultValue);
    }
    if (!!dataCalculatePrice && !isValidating) {
      setCalculatePrice(dataCalculatePrice);
    }
  }, [
    JSON.stringify(dataCalculatePrice),
    isValidating,
    productAvailable,
    JSON.stringify(defaultValue),
  ]);

  const selectPromotion = async (promotion: SelectionPromotion | undefined) => {
    try {
      setLoading(true);
      const newCalculate = await cartServiceV2.calculatePrice({
        sku: payloadAddToCart.sku,
        selectPromotionId: promotion?.promotionId,
        quantity: payloadAddToCart.quantity,
      });
      setCalculatePrice(newCalculate);

      if (promotion === undefined) {
        toast.success?.({
          title: '',
          message: '',
          [isMobile ? 'message' : 'title']: t(
            'Bỏ áp dụng khuyến mãi thành công',
          ),
        });
      } else {
        toast.success?.({
          title: '',
          message: '',
          [isMobile ? 'message' : 'title']: t('Áp dụng khuyến mãi thành công'),
        });
      }
    } catch (err) {
      logger.error(err);
      toast.error?.({
        title: '',
        message: '',
        [isMobile ? 'message' : 'title']: t('Có lỗi xảy ra'),
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    price: {
      finalPrice: calculatePrice?.price,
      ...calculatePrice,
    } as ProductPrice,
    promotionBlocks: calculatePrice?.promotions || [],
    setPayloadAddToCart,
    selectPromotion,
    payloadAddToCart,
    promotionLoading: isValidating,
    priceLoading: isValidating,
    selectPromotionLoading: isLoading,
    selectedPromotion,
    applicableInstallmentMethods: calculatePrice?.applicableInstallmentMethods,
    variants: calculatePrice?.variants,
  };
};
